import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Clock } from "lucide-react";
import { PRICING, buildEnquiryMailto } from "../../data/content";
import { Reveal } from "./Reveal";

const PlanCard = ({ plan, mode, delay }) => (
  <Reveal delay={delay} className="h-full">
    <article
      data-testid={`pricing-card-${plan.id}`}
      className="relative flex h-full flex-col rounded-3xl border border-line bg-surface p-8 transition-transform duration-300 hover:-translate-y-1"
    >
      {plan.badge && (
        <span
          className={`absolute -top-3 left-8 rounded-full border px-3.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
            plan.badge.toLowerCase().includes("free")
              ? "border-gold/50 bg-gold/15 text-gold"
              : "border-jade/40 bg-jade-light text-pine"
          }`}
        >
          {plan.badge}
        </span>
      )}
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-mute">
        {mode === "individual" ? "1-to-1 individual" : "Group lessons"}
      </p>
      <div className="mt-5 flex items-end gap-2">
        <span className="font-serif text-6xl leading-none text-ink">{plan.hours}</span>
        <span className="pb-1 text-sm text-ink-soft">hours</span>
      </div>
      <p className="mt-4 font-serif text-2xl text-pine">
        {plan.total}
        {plan.rate && (
          <span className="ml-2 font-sans text-sm font-normal text-ink-mute">
            · {plan.rate} per hour
          </span>
        )}
      </p>
      {plan.sub && <p className="mt-1 text-xs text-jade">{plan.sub}</p>}
      <ul className="mt-6 flex-1 space-y-3 border-t border-line pt-6">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-ink-soft">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-jade" />
            {f}
          </li>
        ))}
      </ul>
      <a
        href={buildEnquiryMailto({ hours: plan.hours.replace("group-", ""), type: mode })}
        className="btn-primary mt-8 w-full justify-center"
      >
        Enquire for this plan
        <ArrowUpRight className="h-4 w-4" />
      </a>
    </article>
  </Reveal>
);

const NotesCard = () => (
  <Reveal delay={0.24} className="h-full">
    <div className="flex h-full flex-col justify-between rounded-3xl bg-deep p-8 text-paper">
      <div>
        <span className="rounded-full border border-paper/20 px-3.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
          Good to know
        </span>
        <p className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-paper/85">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          Every lesson runs 90 minutes to 2 hours.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-paper/85">
          Flexible schedule — lessons any time, arranged around you.
        </p>
      </div>
      <p className="mt-10 font-arabic text-3xl text-paper/40">أهلاً وسهلاً</p>
    </div>
  </Reveal>
);

export const Pricing = () => {
  const [mode, setMode] = useState("individual");
  const plans = PRICING[mode];

  return (
    <section id="pricing" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <p className="eyebrow">Tuition &amp; packages</p>
              <h2 className="mt-3 font-serif text-3xl tracking-tight text-ink sm:text-4xl md:text-5xl">
                Simple, honest pricing
              </h2>
            </div>
            <div className="flex rounded-full border border-line bg-surface p-1.5">
              {[
                ["individual", "1-to-1 Individual"],
                ["group", "Group"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  data-testid={`pricing-toggle-${value}`}
                  onClick={() => setMode(value)}
                  className={`relative rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                    mode === value ? "text-paper" : "text-ink-soft hover:text-pine"
                  }`}
                >
                  {mode === value && (
                    <motion.span
                      layoutId="pricing-pill"
                      className="absolute inset-0 rounded-full bg-pine"
                      transition={{ type: "spring", stiffness: 320, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 grid gap-6 md:grid-cols-3"
        >
          {plans.map((plan, i) => (
            <PlanCard key={plan.id} plan={plan} mode={mode} delay={i * 0.08} />
          ))}
          {mode === "group" && <NotesCard />}
        </motion.div>

        <Reveal delay={0.1}>
          <p className="mt-10 text-center text-xs uppercase tracking-[0.2em] text-ink-mute">
            Flexible schedule — any time · Cancellation with 48 hours&apos; notice · Lessons
            online or at Wembley Park
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
